import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { FirebaseUser } from '../types/user';
import './stylesheets/SelectedInterviewModal.css';

interface InterviewAttempt {
  id: string;
  answers: any[];
  average_score: number;
  created_at: string;
  duration_minutes: number;
  ended_at: string;
  question_count: number;
  sessionId: string;
  session_id: string;
  source: string;
  specialty: string;
  specialtyId: string;
  started_at: string;
  status: string;
  uid: string;
  updated_at: string;
  user_id: string;
}

interface InterviewProgress {
  answers: Array<{
    category: string;
    feedback: string;
    part: string | number;
    question_id: string;
    question_text: string;
    timestamp: string;
    user_answer: string;
  }>;
  current_index: number;
  current_part_index: number;
  questions: Array<{
    category: string;
    parts: Array<{
      answer_explained: null;
      modelAnswer: string;
      part: string;
      question_text: string;
      rubric: string;
    }>;
    question_id: string;
  }>;
  specialty: string;
  started_at: string;
  user_id: string;
}

interface SelectedInterviewModalProps {
  interview: InterviewAttempt;
  user: FirebaseUser | null;
  onClose: () => void;
}

const SelectedInterviewModal: React.FC<SelectedInterviewModalProps> = ({
  interview,
  user,
  onClose
}) => {
  const [interviewProgress, setInterviewProgress] = useState<InterviewProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '0px'; // Prevent layout shift
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, []);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);

  useEffect(() => {
    loadInterviewProgress();
  }, [interview.sessionId, interview.session_id]);

  const loadInterviewProgress = async () => {
    setLoading(true);
    try {
      const sessionId = interview.sessionId || interview.session_id;
      if (!sessionId) {
        console.error('No session ID found');
        setLoading(false);
        return;
      }

      const progressDocRef = doc(db, 'interviewProgress', sessionId);
      const progressSnapshot = await getDoc(progressDocRef);
      
      if (progressSnapshot.exists()) {
        setInterviewProgress(progressSnapshot.data() as InterviewProgress);
      } else {
        console.error('No interview progress found for session:', sessionId);
      }
    } catch (error) {
      console.error('Error loading interview progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return 'Invalid Date';
    }
  };

  const formatSpecialty = (specialty: string): string => {
    return specialty
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const extractScore = (feedback: string): string => {
    const scoreMatch = feedback.match(/Score:\s*(\d+\/\d+)/);
    return scoreMatch ? scoreMatch[1] : 'N/A';
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-container">
        {/* Header */}
        <div className="modal-header">
          <div className="header-content">
            <h2 className="modal-title">Interview Details</h2>
            <div className={`interview-info-row${isMobile ? ' mobile' : ''}`}>
              <span className="interview-user">
                <strong>User:</strong> {user?.name || user?.email || 'Unknown User'}
              </span>
              <span className="interview-session">
                <strong>Session:</strong> {interview.sessionId || interview.session_id || 'N/A'}
              </span>
              <span className="interview-specialty">
                <strong>Specialty:</strong> {formatSpecialty(interview.specialty || 'N/A')}
              </span>
              <span className="interview-score">
                <strong>Score:</strong> {interview.average_score ? interview.average_score.toFixed(2) : 'N/A'}
              </span>
            </div>
          </div>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div className={`modal-body${isMobile ? ' mobile' : ''}`}>
          {loading ? (
            <div className="no-data">Loading interview details...</div>
          ) : !interviewProgress ? (
            <div className="no-data">No interview progress data found for this session.</div>
          ) : (
            <>
              {/* Interview Metadata */}
              <div className="interview-metadata">
                <div className="metadata-item">
                  <div className="metadata-label">Start Date</div>
                  <div className="metadata-value">
                    {formatDate(interview.started_at)}
                  </div>
                </div>
                <div className="metadata-item">
                  <div className="metadata-label">End Date</div>
                  <div className="metadata-value">
                    {formatDate(interview.ended_at)}
                  </div>
                </div>
                <div className="metadata-item">
                  <div className="metadata-label">Duration</div>
                  <div className="metadata-value">
                    {interview.duration_minutes ? `${interview.duration_minutes.toFixed(1)} min` : 'N/A'}
                  </div>
                </div>
                <div className="metadata-item">
                  <div className="metadata-label">Questions Answered</div>
                  <div className="metadata-value">
                    {interview.question_count || 0}
                  </div>
                </div>
              </div>

              {/* Questions and Answers */}
              <div className="section">
                <h3 className={`section-title${isMobile ? ' mobile' : ''}`}>
                  Questions & Answers ({interviewProgress.answers.length} answers)
                </h3>
                
                <div className="question-grid">
                  {interviewProgress.answers.map((answer, index) => (
                    <div key={index} className="question-card">
                      <div className="question-header">
                        <div className="question-meta">
                          <div className="question-id">{answer.question_id}</div>
                          <div className="question-category">{answer.category}</div>
                        </div>
                        {answer.part && answer.part !== null && (
                          <div className="question-part">Part {answer.part}</div>
                        )}
                      </div>

                      <div className="question-text">
                        {answer.question_text}
                      </div>

                      <div className="answer-section">
                        <div className="answer-label">User Answer:</div>
                        <div className={`user-answer${!answer.user_answer ? ' empty' : ''}`}>
                          {answer.user_answer || 'No answer provided'}
                        </div>
                      </div>

                      {answer.feedback && (
                        <div className="feedback-section">
                          <div className="feedback-label">
                            Feedback (Score: {extractScore(answer.feedback)})
                          </div>
                          <div className="feedback-text">
                            {answer.feedback}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectedInterviewModal;